<script setup lang="ts">
import PredictionResult from "@/views/Calculators/TarotPrediction/SituationPrediction/components/PredictionResult.vue";
import { ROUTE_NAMES } from "@/constants/routeNames";
import { ref } from "vue";
import { TarotSituationPredictionResponse } from "@/types/calculators";
import { fetchTarotSituationPrediction } from "@/api/calculators";
import { useSafeBack } from "@/utils/useSafeBack";
import BackButton from "@/components/buttons/BackButton.vue";
import { useI18n } from "vue-i18n";
import GradientButton from "@/components/buttons/GradientButton.vue";
import PredictionInstruction from "@/views/Calculators/TarotPrediction/SituationPrediction/components/PredictionInstruction.vue";

const { t } = useI18n();
const { safeBack } = useSafeBack();

const requestedSituation = ref<string>("");
const loading = ref<boolean>(false);
const predictionResult = ref<TarotSituationPredictionResponse | null>(null);

async function getSituationPrediction(situation: string) {
  loading.value = true;
  requestedSituation.value = situation;
  try {
    predictionResult.value = await fetchTarotSituationPrediction(situation);
  } finally {
    loading.value = false;
  }
}

function handleBack() {
  if (predictionResult.value) {
    predictionResult.value = null;
    return;
  }
  safeBack(ROUTE_NAMES.TAROT);
}
</script>

<template>
  <div
    class="flex flex-col gap-6 h-full xl:h-screen w-full pt-6 xl:pt-[88px] pb-6 px-4"
  >
    <div class="relative items-center md:hidden">
      <div class="absolute left-0">
        <BackButton @clicked="handleBack" />
      </div>
      <h2 class="text-2xl font-medium leading-[32px] text-brown">
        {{ t("calculators.tarot.situation-prediction.title") }}
      </h2>
    </div>
    <div
      class="text-left md:mx-auto py-6 px-4 md:px-10 bg-white rounded-[20px] flex flex-col items-center w-full md:max-w-[812px] h-full min-h-0 overflow-y-auto no-scrollbar"
    >
      <div
        class="relative hidden md:flex justify-center items-center w-full min-h-11 mb-2 px-10"
      >
        <div class="absolute left-0">
          <BackButton @clicked="handleBack" />
        </div>
        <h2 class="mx-auto text-2xl font-semibold leading-[32px] text-brown">
          {{
            predictionResult
              ? t("calculators.tarot.situation-prediction.result-title")
              : t("calculators.tarot.situation-prediction.title")
          }}
        </h2>
      </div>
      <p
        v-if="!predictionResult"
        class="text-sm font-normal leading-[20px] text-muted italic mb-10"
      >
        {{ t("calculators.tarot.situation-prediction.short-tip") }}
      </p>
      <p
        v-if="!predictionResult"
        class="font-normal text-base leading-[20px] text-black w-full mb-4"
      >
        {{ t("calculators.tarot.situation-prediction.input-label") }}
      </p>
      <textarea
        v-if="!predictionResult"
        class="rounded-[20px] border border-muted w-full min-h-28 mb-10 p-4 resize-none focus:outline-none focus:ring-0 focus:border-muted"
        :placeholder="
          t('calculators.tarot.situation-prediction.input-placeholder')
        "
      ></textarea>
      <GradientButton
        v-if="!predictionResult"
        class="w-[353px] min-h-14 mb-10 hidden md:flex"
        bg-color="bg-brown-gradient"
        hover-bg-color="bg-orange-gradient"
        :text="t('calculators.tarot.situation-prediction.get-prediction-btn')"
        @click="getSituationPrediction"
        :is-loading="loading"
      />
      <GradientButton
        v-if="!predictionResult"
        class="w-[247px] min-h-14 mb-10 md:hidden"
        bg-color="bg-brown-gradient"
        hover-bg-color="bg-orange-gradient"
        :text="
          t('calculators.tarot.situation-prediction.get-prediction-btn-short')
        "
        @click="getSituationPrediction"
        :is-loading="loading"
      />
      <PredictionResult v-else :result="predictionResult" />
      <PredictionInstruction v-if="!predictionResult" />
    </div>
  </div>
</template>
